import { useEffect, useMemo, useState } from 'react';
import { Button, Input, useToast, Skeleton } from '../../../shared/components';
import { getTurnoGeoRepository } from '../../../shared/services/turnoGeo/repositoryFactory';
import { TurnoGeoParameter } from '../../../shared/services/turnoGeo/types';
import { InfoTip } from './InfoTip';
import { DAY_NAMES, DAY_ORDER, daysToCsv, parseDays } from '../jornadaUtil';

const repo = getTurnoGeoRepository();

const CATEGORY_TITLES: Record<string, string> = {
  GENERAL: 'General',
  FOTO: 'Foto',
  UBICACION: 'Ubicación (GPS)',
  RED: 'Red (IP)',
  CASA: 'Casa y oficina registradas',
  JORNADA: 'Jornada, almuerzo y horario general',
  CONTROL: 'Control y auditoría',
  TOQUES: 'Toques aleatorios de ubicación',
  HORAS: 'Horas, recargos y festivos',
};

const TIME_CODES = new Set(['WORK_START_TIME', 'WORK_END_TIME', 'LUNCH_START_TIME', 'LUNCH_END_TIME']);

/**
 * Configuración > Parámetros (ParametrosTurnoGeo). La pantalla se arma sola
 * con las filas de la tabla: agregar un parámetro nuevo en base de datos lo
 * muestra aquí sin tocar el código. Ningún umbral queda quemado.
 */
export function ParametersTab() {
  const toast = useToast();
  const [params, setParams] = useState<TurnoGeoParameter[] | null>(null);
  const [edits, setEdits] = useState<Record<string, string>>({});
  const [isSaving, setIsSaving] = useState(false);

  const load = () =>
    repo.listParameters().then((rows) => {
      setParams(rows);
      setEdits({});
    });
  useEffect(() => {
    load();
  }, []);

  const grouped = useMemo(() => {
    const map = new Map<string, TurnoGeoParameter[]>();
    for (const p of params ?? []) map.set(p.category, [...(map.get(p.category) ?? []), p]);
    return [...map.entries()];
  }, [params]);

  if (!params) return <Skeleton className="h-72 rounded-2xl" />;

  const valueOf = (p: TurnoGeoParameter) => edits[p.code] ?? p.value;
  const setValue = (p: TurnoGeoParameter, value: string) => setEdits((e) => ({ ...e, [p.code]: value }));
  const dirty = params.filter((p) => edits[p.code] !== undefined && edits[p.code] !== p.value);

  const save = async () => {
    setIsSaving(true);
    try {
      for (const p of dirty) {
        try {
          await repo.updateParameter(p.code, edits[p.code]);
        } catch (err) {
          toast.show(err instanceof Error ? err.message : `No se pudo guardar "${p.label}".`, 'error');
          await load();
          return;
        }
      }
      toast.show(dirty.length === 1 ? 'Parámetro guardado.' : `${dirty.length} parámetros guardados.`);
      await load();
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      {grouped.map(([category, rows]) => (
        <section key={category} className="space-y-3">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-ink-400">{CATEGORY_TITLES[category] ?? category}</h3>
          {rows.map((p) => {
            const help = p.description ? <InfoTip title={p.label} text={p.description} /> : null;
            if (p.valueType === 'BOOL') {
              return (
                <label key={p.code} className="flex items-center gap-3 rounded-xl border border-ink-100 bg-surface p-4">
                  <input
                    type="checkbox"
                    checked={valueOf(p) === 'true'}
                    onChange={(e) => setValue(p, e.target.checked ? 'true' : 'false')}
                    className="h-5 w-5 rounded border-ink-300"
                  />
                  <div className="min-w-0 flex-1">
                    <p className="flex items-center gap-2 text-sm font-semibold text-ink-800">{p.label}{help}</p>
                    {p.description && <p className="text-xs text-ink-400">{p.description}</p>}
                  </div>
                </label>
              );
            }
            if (p.code === 'WORK_DAYS') {
              const selected = parseDays(valueOf(p));
              const toggle = (day: number) => setValue(p, daysToCsv(selected.includes(day) ? selected.filter((d) => d !== day) : [...selected, day]));
              return (
                <div key={p.code} className="rounded-xl border border-ink-100 bg-surface p-4">
                  <p className="flex items-center gap-2 text-sm font-semibold text-ink-800">{p.label}{help}</p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {DAY_ORDER.map((day) => (
                      <label
                        key={day}
                        className={['flex cursor-pointer items-center gap-2 rounded-xl border px-3 py-2 text-sm', selected.includes(day) ? 'border-brand-400 bg-brand-light text-brand-700' : 'border-ink-200 text-ink-600'].join(' ')}
                      >
                        <input type="checkbox" className="h-4 w-4" checked={selected.includes(day)} onChange={() => toggle(day)} />
                        {DAY_NAMES[day]}
                      </label>
                    ))}
                  </div>
                  <p className="mt-2 text-xs text-ink-400">Por defecto lunes a viernes. Marcar en un día no laborado queda registrado para auditar.</p>
                </div>
              );
            }
            return (
              <div key={p.code}>
                <p className="mb-1.5 flex items-center gap-2 text-sm font-medium text-ink-700">{p.label}{help}</p>
                <Input
                  aria-label={p.label}
                  type={TIME_CODES.has(p.code) ? 'time' : p.valueType === 'INT' ? 'number' : 'text'}
                  min={p.minValue ?? undefined}
                  max={p.maxValue ?? undefined}
                  value={valueOf(p)}
                  onChange={(e) => setValue(p, e.target.value)}
                  hint={[p.description, p.valueType === 'INT' && p.minValue != null && p.maxValue != null ? `Rango permitido: ${p.minValue} a ${p.maxValue}.` : null].filter(Boolean).join(' ')}
                />
              </div>
            );
          })}
        </section>
      ))}

      <Button fullWidth onClick={save} isLoading={isSaving} disabled={dirty.length === 0}>
        {dirty.length === 0 ? 'Sin cambios' : `Guardar ${dirty.length} cambio${dirty.length === 1 ? '' : 's'}`}
      </Button>
    </div>
  );
}
